import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type FormEventHandler, type ReactNode } from 'react';

import { AppButton, AppCard, WhatsAppButton } from '@/components/atoms';
import { ApiError } from '@/services/ApiError';

type Props = {
  title: string;
  /** Target of the enclosing Section's `aria-labelledby`, when the form's own
   *  heading is the band's heading. */
  titleId?: string;
  description?: string;
  submitLabel: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  isPending: boolean;
  error: unknown;
  /** The reference number once it has been sent. */
  receipt: string | null;
  children: ReactNode;
  /** Message used by the WhatsApp alternative beneath the form. */
  whatsappMessage: string;
};

/**
 * The frame every enquiry form shares: loading, failure, success and the
 * duplicate-submission guard (§39). A form never submits twice, because the
 * button is disabled while a request is in flight.
 */
export function EnquiryFormShell({
  title,
  titleId,
  description,
  submitLabel,
  onSubmit,
  isPending,
  error,
  receipt,
  children,
  whatsappMessage,
}: Props) {
  if (receipt) {
    return (
      <AppCard sx={{ p: { xs: 6, md: 10 } }}>
        <Stack spacing={4} alignItems="flex-start" role="status">
          <CheckCircleOutline aria-hidden sx={{ fontSize: 48, color: 'success.main' }} />
          <Typography variant="h4" component="h2">
            Request received
          </Typography>
          <Typography>
            Your reference is{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {receipt}
            </Typography>
            . Our team will contact you on the number you gave us. Nothing has been paid or
            reserved.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ width: '100%' }}>
            <WhatsAppButton message={whatsappMessage}>Continue on WhatsApp</WhatsAppButton>
            <AppButton to="/cars" appVariant="secondary">
              Browse cars
            </AppButton>
          </Stack>
        </Stack>
      </AppCard>
    );
  }

  return (
    <AppCard sx={{ p: { xs: 6, md: 10 } }}>
      <Stack component="form" onSubmit={onSubmit} noValidate spacing={6}>
        <Stack spacing={2}>
          <Typography variant="h4" component="h2" {...(titleId ? { id: titleId } : {})}>
            {title}
          </Typography>
          {description && <Typography color="text.secondary">{description}</Typography>}
        </Stack>

        {children}

        {Boolean(error) && (
          <Alert severity="error">
            {error instanceof ApiError
              ? error.message
              : 'We could not send your request. Please try again.'}
          </Alert>
        )}

        <Stack spacing={3}>
          <AppButton
            type="submit"
            size="large"
            disabled={isPending}
            sx={{ alignSelf: 'flex-start' }}
          >
            {isPending ? 'Sending…' : submitLabel}
          </AppButton>
          <Typography variant="body2" color="text.secondary">
            Sending this request does not constitute a purchase, a reservation or a deposit. We use
            your details only to reply to you.
          </Typography>
          <WhatsAppButton message={whatsappMessage} size="small" sx={{ alignSelf: 'flex-start' }}>
            Or message us on WhatsApp
          </WhatsAppButton>
        </Stack>
      </Stack>
    </AppCard>
  );
}
